import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/src/lib/supabase';
import { log } from '@/src/lib/logger';
import * as pdfjsLib from 'pdfjs-dist';

export interface DrawerContextFile {
  name: string;
  path: string;
  size: number;
  updated_at: string;
}

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

async function extractTextFromPDF(file: File): Promise<string> {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  const pages: string[] = [];
  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    const text = content.items.map((item: any) => ('str' in item ? item.str + (item.hasEOL ? '\n' : ' ') : '')).join('').replace(/[ \t]+\n/g, '\n');
    pages.push(text);
  }
  return pages.join('\n\n');
}
export function useDrawerContexts(venueId: string | null) {
  const [contexts, setContexts] = useState<Record<string, string>>({});
  const [filesByDrawer, setFilesByDrawer] = useState<Record<string, DrawerContextFile[]>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!venueId) {
      setContexts({});
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const { data, error: err } = await supabase
        .from('venues')
        .select('drawer_contexts')
        .eq('id', venueId)
        .maybeSingle();
      if (cancelled) return;
      if (err) setError(err.message);
      else {
        setContexts((data?.drawer_contexts as Record<string, string> | null) ?? {});
        setError(null);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [venueId]);

  const saveDrawer = useCallback(
    async (drawerId: string, text: string) => {
      if (!venueId) return { error: 'no venue' };
      setSaving(true);
      const next = { ...contexts };
      if (text.trim().length === 0) delete next[drawerId];
      else next[drawerId] = text;
      const { error: err } = await supabase
        .from('venues')
        .update({ drawer_contexts: next })
        .eq('id', venueId);
      setSaving(false);
      if (err) {
        log.warn('saveDrawer failed', { code: err.code, drawerId });
        return { error: err.message };
      }
      setContexts(next);
      return { error: null };
    },
    [venueId, contexts],
  );

  const loadFiles = useCallback(
    async (drawerId: string) => {
      if (!venueId) return;
      const folder = `${venueId}/${drawerId}`;
      const { data, error: err } = await supabase.storage
        .from('drawer-context')
        .list(folder, { limit: 50, sortBy: { column: 'updated_at', order: 'desc' } });
      if (err) {
        log.warn('drawer files list failed', { code: err.message, drawerId });
        return;
      }
      const mapped: DrawerContextFile[] = (data ?? []).map((f) => ({
        name: f.name,
        path: `${folder}/${f.name}`,
        size: (f.metadata?.size as number | undefined) ?? 0,
        updated_at: f.updated_at ?? f.created_at ?? '',
      }));
      setFilesByDrawer((prev) => ({ ...prev, [drawerId]: mapped }));
    },
    [venueId],
  );

  const uploadFile = useCallback(
    async (drawerId: string, file: File) => {
      if (!venueId) return { error: 'no venue' };
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `${venueId}/${drawerId}/${Date.now()}-${safeName}`;
      const { error: err } = await supabase.storage.from('drawer-context').upload(path, file, {
        contentType: file.type || undefined,
        cacheControl: '3600',
        upsert: false,
      });
      if (err) return { error: err.message };
      await loadFiles(drawerId);
  
      // Auto-extract text from PDFs
      if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
        console.log('PDF detected, starting extraction:', file.name, file.type);
        try {
          const text = await extractTextFromPDF(file);
          console.log('Extracted text length:', text.length, 'preview:', text.substring(0, 200));
          if (text.trim()) {
            const { data } = await supabase
              .from('venues')
              .select('drawer_contexts')
              .eq('id', venueId)
              .maybeSingle();
            console.log('Fresh contexts:', data);
            const fresh = (data?.drawer_contexts as Record<string, string> | null) ?? {};
            const existing = fresh[drawerId] ?? '';
            const separator = existing ? '\n\n---\n\n' : '';
            const updated = `${existing}${separator}📄 ${file.name}:\n${text}`;
            const next = { ...fresh, [drawerId]: updated };
            const { error: saveErr } = await supabase.from('venues').update({ drawer_contexts: next }).eq('id', venueId);
            console.log('Save result:', saveErr);
            setContexts(next);
          }
        } catch (e) {
          console.error('PDF extraction error:', e);
        }
      }
  
      return { error: null };
    },
    [venueId, loadFiles],
  );

  const deleteFile = useCallback(
    async (drawerId: string, path: string) => {
      const { error: err } = await supabase.storage.from('drawer-context').remove([path]);
      if (err) return { error: err.message };
      await loadFiles(drawerId);
      return { error: null };
    },
    [loadFiles],
  );

  return {
    contexts,
    filesByDrawer,
    loading,
    saving,
    error,
    saveDrawer,
    loadFiles,
    uploadFile,
    deleteFile,
  };
}
